/**
 * Master Pixel Links Configuration
 * This file contains all the logic for generating master pixel links
 * Can be used across different components
 */

export interface LinkConfig {
    title: string;
    path: string;
    basePath?: string;
    type?: string;
    allowedUids?: number[];
}

export const PATHS = {
    TICLI: '/office/ticli/',
    OFFICE: '/office/',
    AJAX: '/ajax/api/',
} as const;

export const createLink = (domain: string, {path, basePath, type, title}: LinkConfig, tickchakToken?: string) => {
    if(path === 'title') {
        return {title, link: ''};
    }

    // If path is a full URL, use it directly
    let link: string;
    if(path.startsWith('http://') || path.startsWith('https://')) {
        link = path;
    } else {
        // Use basePath if explicitly set (even if empty), otherwise default to PATHS.TICLI
        const effectiveBasePath = basePath !== undefined ? basePath : PATHS.TICLI;
        link = `${domain}${effectiveBasePath}${path}`;
    }

    // Add type parameter if exists
    if(type) {
        link += `?type=${type}`;
    }

    // Add tickchak token if in local environment
    if(process.env.REACT_APP_ENV === 'local' && tickchakToken) {
        // Use ? if no query params exist, & if they do
        const separator = link.includes('?') ? '&' : '?';
        link += `${separator}tickchak_token=${tickchakToken}&tickchakNewApp`;
    }

    return {title, link};
};

export function getMasterPixelLinks(domain: string, tickchakToken?: string, currentUid?: number) {
    const allLinks: LinkConfig[] = [
        // Default links available to all masters
        {title: 'מסופים', path: 'title'},
        {title: 'הוספת מסוף חדש', path: 'mudul'},
        {title: 'ניהול מסופים באירועים', path: 'update_supplier'},
        {title: 'אירוע חריג! החלפת מסוף כללי בטיקצאק!', path: 'changeSupplierTickchak', allowedUids: [1555, 2069, 324, 404, 8176]},

        {title: 'פיקסלים', path: 'title'},
        {title: 'הוספת פיקסלים חכמה לאירועים', path: 'known_pixels'},
        {title: 'חיפוש פיקסלים', path: 'pixels_list'},
        {title: 'פיקסלים לחבילה (חדש!)', path: 'package_pixels'},
        {title: 'יצירת קישור והקפצה ללייב אירוע', path: 'new_events_til'},

        {title: 'גיבצ\'אק', path: 'title'},
        {title: 'ניהול אירועי גיבצ\'אק מודבקים', path: 'sticky_events', basePath: PATHS.OFFICE},
        {title: 'החרגות חסימות אייפריים בגיבצ\'אק', path: 'new_model', type: 'givechak_dangerous_activity'},
        {title: 'חיבור בין התרמות גיבצ\'אק', path: 'givechak_connect_events'},

        {title: 'חיבורים', path: 'title'},
        {title: 'חיבור בין אירועים מרובים לאפליקציה', path: 'new_model_terminal'},
        {title: 'הוספת חיבור לאירוע לנדרים', path: 'new_model', type: 'nedarim'},
        {title: 'הוספת חיבור לאירוע לרישומון', path: 'new_model', type: 'rishumon'},
        {title: 'חיבור והרשאה לאירוע להתממשקות עם ימות המשיח', path: 'new_model', type: 'yemot_hamashiach'},

        {title: 'ווצאפ', path: 'title'},
        {title: 'ניהול תבניות וואטסאפ', path: 'https://services.tickchak.co.il/mailing/whatsapp/whatsapp-templates.html', basePath: ''},
        {title: 'ניהול מספרי וואצאפ', path: 'https://services.tickchak.co.il/mailing/whatsapp/whatsapp-phones.html', basePath: ''},
        {title: 'אירוע עם שליחה בווצאפ ישן', path: 'new_model', type: 'whatsapp'},
        {title: 'חבילה עם שליחה בווצאפ ישן', path: 'new_model', type: 'whatsapp_package'},
        {title: 'אירוע עם שליחה בווצאפ ישן CRM', path: 'new_model', type: 'whatsapp_crm'},
        {title: 'חבילה עם שליחה בווצאפ ישן CRM', path: 'new_model', type: 'whatsapp_package_crm'},
        {title: 'אירוע עם שליחה בווצאפ JONI', path: 'new_model', type: 'whatsapp_joni'},
        {title: 'חבילה עם שליחה בווצאפ JONI', path: 'new_model', type: 'whatsapp_joni_package'},
        {title: 'אירועים עם הוספה אוטומטית לקבוצות וואצאפ', path: 'new_model', type: 'add_to_whatsapp_group'},
        {title: 'חשבונות עם הוספה אוטומטית לקבוצות וואצאפ', path: 'new_model', type: 'add_to_whatsapp_group_package'},
        {title: 'ניהול תבניות וואצאפ (ישן)', path: 'whatsapp_templates', type: 'whatsapp_templates'},

        {title: 'דשבורדים', path: 'title'},
        {title: 'לוח בקרה — תור ההודעות', path: 'https://services.tickchak.co.il/mailing/dashboard', basePath: ''},
        {title: 'צפה בדירוגי משתמשים', path: 'show_ratings'},
        {title: 'דשבורד', path: 'dashboard', basePath: PATHS.OFFICE, allowedUids: [1555, 2069]},
        {title: 'דשבורד ישן', path: 'MASTER?t=090TUENWQ3pmNZdHS1NUOsRzUxNTUW50d', allowedUids: [1555, 2069]},
        {title: 'מלכת הטיפשים דגם מתקדם', path: 'invoice_all', basePath: PATHS.OFFICE},

        {title: 'לייב', path: 'title'},
        {title: 'ניהול אמנים חדש!', path: 'manage_live', basePath: PATHS.AJAX, allowedUids: [1555, 2069]},
        {title: 'דפי אולמות קביעת עיר', path: 'page_edit_live', allowedUids: [7, 2069, 1555, 4712]},
        {title: 'דפי לייב סידור כותרות', path: 'live_google_sco'},

        {title: 'חסימות', path: 'title'},
        {title: 'חסימות IP', path: 'blocked_ip_allowed', allowedUids: [7, 2069, 7423, 1555, 4712, 9860, 8176, 16134, 324, 404]},
        {title: 'היסטוריית חסימות IP', path: 'blocked_ip_archive', allowedUids: [7, 2069, 7423, 1555, 4712, 9860, 8176, 16134]},
        {title: 'חסימות יוזר', path: 'blocked_user', allowedUids: [2069, 1555, 7423, 8176, 16134]},

        {title: 'שדות מותאמים אישית', path: 'title'},
        {title: 'הוספת שדה רשימה ארוכה', path: 'create_custom_field_list'},
        {title: 'שדות מותאמים אישית לחבילה', path: 'package_custom_fields'},

        {title: 'מוצר דחיפה', path: 'title'},
        {title: 'הגדרות מוצר דחיפה', path: 'update_specials'},
        
        {title: 'קופונים', path: 'title'},
        {title: 'חיפוש שוברים', path: 'search_voucher'},

        {title: 'מסאטר יוזר', path: 'title'},
        {title: 'מאסטר יוזר רשימה', path: 'master_user_manage?page=users'},
        {title: 'מאסטר יוזר תפקידים', path: 'master_user_manage?page=roles'},

        {title: 'נוספים', path: 'title'},
        {title: 'ניהול התראות מערכת', path: 'system_alerts'},
        {title: 'חסימת רכישה כפולה באירועים בחבילה 1912', path: 'pixel_limit_orders_package_1912'},
        {title: 'באג בשליחת סמס! החלפת חברת שליחת סמס!', path: 'changeTickchakSmsSender', allowedUids: [1555, 2069]},
        {title: 'הורדת אקסל עסקאות', path: 'download_excel_transactions', allowedUids: [7, 2069]},
        {title: 'הקמה ועריכת וובהוק', path: 'webhook_management'},
        {title: 'תרגומים', path: 'translate', allowedUids: [2069]},
        {title: 'שינוי חבילה של אירוע', path: 'change_event_package'},
        {title: 'איפוס מחיר הזמנה עסקת ייבוא / שריון בלבד', path: 'reset_order_price'},
        {title: 'הוספת הזמנה שנפלה', path: 'insert_orders', allowedUids: [2069, 1555, 7423, 8176, 16134]},
        {title: 'עדכון מחיר כל הכרטיסים באירוע', path: 'update_tickets_price'},
        // {title: 'אירועים עם אפשרות ביטול מתוך הכרטיס', path: 'new_model', type: 'cancel_from_ticket'},
        // {title: 'חשבונות עם אפשרות ביטול מתוך הכרטיס', path: 'new_model', type: 'cancel_from_ticket_package'},
    ];

    // Filter links based on current UID
    const filteredLinks = allLinks.filter(link =>
        !link.allowedUids || (currentUid && link.allowedUids.includes(currentUid))
    );
    
    return filteredLinks.map(config => createLink(domain, config, tickchakToken));
}
